import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { Reveal, KineticText } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
    {
        icon: Phone,
        label: "Call Us",
        value: "+91 6207984279",
        href: "tel:+916207984279",
        testid: "contact-call-card",
    },
    {
        icon: Mail,
        label: "Email Us",
        value: "zyne6431@gmail.com",
        href: "mailto:zyne6431@gmail.com",
        testid: "contact-email-card",
    },
    {
        icon: MessageCircle,
        label: "WhatsApp",
        value: "+91 6207984279",
        href: "https://wa.me/916207984279",
        testid: "contact-whatsapp-card",
    },
];

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            const res = await axios.post(`${API}/contact`, form);
            toast.success(res.data.message || "Message sent!");
            setSent(true);
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Could not send your message. Please call us at 6207984279.";
            toast.error(
                typeof detail === "string" ? detail : "Please check your details and try again."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <main className="pt-36 sm:pt-44 pb-24">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <Reveal>
                    <p className="eyebrow">Contact Us</p>
                </Reveal>
                <h1 className="mt-7 f-display text-[clamp(2.5rem,7vw,5.5rem)] font-black uppercase leading-[0.98] tracking-tighter text-white">
                    <KineticText
                        delay={0.2}
                        lines={[
                            "Let's talk about",
                            <span key="a" className="f-serif normal-case font-medium text-[#FF5500]">
                                your future.
                            </span>,
                        ]}
                    />
                </h1>
                <Reveal delay={0.1}>
                    <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400">
                        Questions about batches, admissions or subjects? Reach
                        us directly — or leave a message and we will get back
                        to you.
                    </p>
                </Reveal>

                <div className="mt-16 grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
                    <div className="space-y-4">
                        {INFO.map((c, i) => (
                            <Reveal key={i} delay={i * 0.08}>
                                <a
                                    href={c.href}
                                    data-testid={c.testid}
                                    target={c.href.startsWith("http") ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="card-dark flex items-center gap-5 p-6"
                                >
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FF5500]/12 border border-[#FF5500]/25">
                                        <c.icon size={20} className="text-[#FF5500]" />
                                    </span>
                                    <span>
                                        <span className="block f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                            {c.label}
                                        </span>
                                        <span className="mt-1 block f-display font-bold text-white break-all">
                                            {c.value}
                                        </span>
                                    </span>
                                </a>
                            </Reveal>
                        ))}

                        <Reveal delay={0.3}>
                            <div className="rounded-2xl border border-white/8 bg-[#0A0E16] p-6">
                                <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                    Office
                                </p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                                    Vivek Coaching Centre
                                    <br />
                                    Walk in for admissions enquiry — call ahead
                                    and we will keep a slot ready for you.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.12}>
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="card-dark !bg-[#0F131D]/80 p-7 sm:p-9"
                        >
                            <h2 className="f-display text-xl font-extrabold tracking-tight text-white">
                                Send us a message
                            </h2>

                            <div className="mt-7 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="name" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Your Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        data-testid="contact-input-name"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder="Full name"
                                        className="field mt-2"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="phone" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Phone
                                    </label>
                                    <input
                                        id="phone"
                                        name="phone"
                                        data-testid="contact-input-phone"
                                        required
                                        minLength={10}
                                        maxLength={15}
                                        value={form.phone}
                                        onChange={onChange}
                                        placeholder="10-digit mobile number"
                                        className="field mt-2"
                                    />
                                </div>
                            </div>

                            <div className="mt-5">
                                <label htmlFor="email" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    data-testid="contact-input-email"
                                    required
                                    value={form.email}
                                    onChange={onChange}
                                    placeholder="you@example.com"
                                    className="field mt-2"
                                />
                            </div>

                            <div className="mt-5">
                                <label htmlFor="message" className="f-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    data-testid="contact-input-message"
                                    required
                                    minLength={5}
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    placeholder="Which class or exam is this about?"
                                    className="field mt-2 resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="contact-submit-button"
                                className="btn-primary mt-8 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {sending ? "Sending…" : "Send Message"}
                                <Send size={16} />
                            </button>

                            {sent && (
                                <div
                                    data-testid="contact-success-alert"
                                    className="mt-5 flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3.5"
                                >
                                    <CheckCircle2 size={18} className="shrink-0 text-emerald-400" />
                                    <p className="text-sm text-emerald-300">
                                        Thank you! Your message has reached us —
                                        we will get back to you shortly.
                                    </p>
                                </div>
                            )}
                        </form>
                    </Reveal>
                </div>
            </div>
        </main>
    );
};

export default Contact;
